// UI strings for the pronunciation coach's mouth pictures (app/src/coach/mouth/, work package C of
// docs/design/AANTAMISVALMENTAJA.md): the three views' names, the parts' names for the guide pages'
// labels, the honest "your likely position" label, the palate view's "behind the plate" estimate, the
// views' text equivalents (built from the geometry's facts: coach/mouth/describe.ts) and the credits
// for the anatomy data (MOCHA-TIMIT, USC rtMRI) for the credits page. Finnish (default) and English
// side by side; en has the same shape. Style: docs/FINNISH-STYLE.md (terms as in Kielioppi § 1:
// hammasvalli, kova suulaki, pehmeä suulaki, kitakieleke, kielen kärki / selkä / takaosa).
import { defineArea } from '../area';

export const coachMouthFi = {
  views: { side: 'Sivukuva', palate: 'Suulaki alhaalta', lips: 'Huulet edestä' } as Record<'side' | 'palate' | 'lips', string>,
  /** Labels on the guide pages' side view. */
  parts: {
    lips: 'huulet',
    upperIncisor: 'yläetuhammas',
    ridge: 'hammasvalli',
    hardPalate: 'kova suulaki',
    velum: 'pehmeä suulaki',
    uvula: 'kitakieleke',
    tip: 'kielen kärki',
    body: 'kielen selkä',
    root: 'kielen tyvi',
    epiglottis: 'kurkunkansi',
    folds: 'äänihuulet',
    jaw: 'alaleuka',
    nasal: 'nenäontelo',
    pharynx: 'nielu',
  } as Record<string, string>,
  /** Over the person's likely position (the ghost): it is an estimate from the sound, not a picture of their mouth. */
  ghost: 'arvio äänestä – ei kuva suustasi',
  /** On the palate view where contact lies behind the measuring plate (k, g, gh, q). */
  behindPlate: 'arvio, ei mitattu',
  /** On a trill, which a screen can only show slowed down. */
  slowed: 'hidastettu',
  /** Under a still picture of a trill: the pale band is the path the tip moves along. */
  trillRange: 'kielen kärjen liikealue',
  glottis: { open: 'äänihuulet erillään', vibrating: 'äänihuulet värisevät', closed: 'äänihuulet kiinni' } as Record<'open' | 'vibrating' | 'closed', string>,
  /** Text equivalents of the pictures, sentence by sentence. */
  say: {
    side: 'Sivukuva suusta.',
    palate: 'Suulaki alhaalta katsottuna.',
    lips: 'Huulet edestä.',
    part: { tip: 'Kielen kärki', blade: 'Kielen etuosa', front: 'Kielen keskiosa', back: 'Kielen takaosa', root: 'Kielen tyvi' } as Record<string, string>,
    /** The place in the partitive, before "vasten". */
    against: {
      teeth: 'hampaita',
      ridge: 'hammasvallia',
      postalveolar: 'hammasvallin takaosaa',
      palate: 'kovaa suulakea',
      velum: 'pehmeää suulakea',
      uvula: 'kitakielekettä',
      pharynx: 'nielun takaseinää',
    } as Record<string, string>,
    /** The place in the genitive, before "lähellä". */
    near: {
      teeth: 'hampaiden',
      ridge: 'hammasvallin',
      postalveolar: 'hammasvallin takaosan',
      palate: 'kovan suulaen',
      velum: 'pehmeän suulaen',
      uvula: 'kitakielekkeen',
      pharynx: 'nielun takaseinän',
    } as Record<string, string>,
    closed: (part: string, against: string) => `${part} painuu ${against} vasten ja sulkee ilmavirran.`,
    /** A lateral (l, lj): the middle is closed, the next sentence says where the air gets out. */
    closedMid: (part: string, against: string) => `${part} painuu ${against} vasten ja sulkee ilmavirran keskeltä.`,
    narrow: (part: string, near: string) => `${part} on ${near} lähellä: ilma pääsee kapeasta raosta.`,
    lipsClosed: 'Huulet ovat kiinni.',
    labiodental: 'Alahuuli on yläetuhampaita vasten.',
    open: 'Ilma pääsee vapaasti suun läpi.',
    groove: 'Kielen keskelle jää kapea ura.',
    sideOne: 'Ilma pääsee ulos toiselta sivulta.',
    sideBoth: 'Ilma pääsee ulos kummaltakin sivulta.',
    tipDown: 'Kielen kärki on alahampaiden takana.',
    rounded: 'Huulet ovat pyöreinä.',
    spread: 'Huulet ovat levällään.',
    nasal: 'Pehmeä suulaki on alhaalla, joten ilma kulkee nenän kautta.',
    voice: { open: 'Äänihuulet ovat erillään: äänne on soinniton.', vibrating: 'Äänihuulet värisevät.', closed: 'Äänihuulet ovat kiinni.' } as Record<
      'open' | 'vibrating' | 'closed',
      string
    >,
    trill: 'Kielen kärki värisee hammasvallia vasten.',
    /** Palate view: what the tongue touches. */
    touches: (what: string) => `Kieli koskettaa ${what}.`,
    noTouch: 'Kieli ei kosketa kovaa suulakea.',
    zones: {
      ridge: 'hammasvallia',
      behindRidge: 'hammasvallin takaosaa',
      palate: 'kovaa suulakea',
      back: 'kovan suulaen takareunaa',
      sides: 'suulaen sivuja',
    } as Record<string, string>,
    and: 'ja',
    behind: 'Takana kieli koskettaa pehmeää suulakea (arvio, ei mitattu).',
    teethShow: 'Yläetuhampaat näkyvät.',
    mouthOpen: 'Huulten välissä on rako.',
  },
  credits: {
    title: 'Suun kuvien lähteet',
    mocha:
      'Suulakikuvien kosketuskartat on laskettu MOCHA-TIMIT-aineistosta, jossa kielen kosketus suulakeen on mitattu suulakilevyllä: puhujat msak0 ja fsew0, Alan Wrench, Queen Margaret University College 1999. Aineistoa on muokattu: kuvissa on kunkin äänteen keskiarvo, ei alkuperäisiä mittauksia. Saa käyttää vain tutkimukseen, opetukseen ja omaan käyttöön.',
    usc: 'Sivukuvan mittasuhteet ja kielen asennot on mitattu USC:n 75 puhujan reaaliaikaisista magneettikuvista (Lim ym. 2021), lisenssi CC BY 4.0. Kuvista on piirretty ääriviivat; itse kuvia ei ole sovelluksessa.',
    literature: 'Äänteissä, joita englannissa ei ole (gh, q, lj, ñ, täryt), asennot on piirretty foneettisen kirjallisuuden kuvausten mukaan.',
    licence: 'MOCHA-TIMIT-aineiston lisenssi',
  },
};

export const coachMouthEn: typeof coachMouthFi = {
  views: { side: 'Side view', palate: 'Palate from below', lips: 'Lips from the front' },
  parts: {
    lips: 'lips',
    upperIncisor: 'upper incisor',
    ridge: 'alveolar ridge',
    hardPalate: 'hard palate',
    velum: 'soft palate',
    uvula: 'uvula',
    tip: 'tongue tip',
    body: 'tongue body',
    root: 'tongue root',
    epiglottis: 'epiglottis',
    folds: 'vocal folds',
    jaw: 'lower jaw',
    nasal: 'nasal cavity',
    pharynx: 'pharynx',
  },
  ghost: 'estimated from the sound – not a picture of your mouth',
  behindPlate: 'estimate, not measured',
  slowed: 'slowed down',
  trillRange: "the tongue tip's range of motion",
  glottis: { open: 'vocal folds apart', vibrating: 'vocal folds vibrating', closed: 'vocal folds closed' },
  say: {
    side: 'Side view of the mouth.',
    palate: 'The palate seen from below.',
    lips: 'The lips from the front.',
    part: {
      tip: 'The tongue tip',
      blade: 'The front of the tongue',
      front: 'The middle of the tongue',
      back: 'The back of the tongue',
      root: 'The tongue root',
    },
    against: {
      teeth: 'the teeth',
      ridge: 'the alveolar ridge',
      postalveolar: 'the area just behind the ridge',
      palate: 'the hard palate',
      velum: 'the soft palate',
      uvula: 'the uvula',
      pharynx: 'the back wall of the pharynx',
    },
    near: {
      teeth: 'the teeth',
      ridge: 'the alveolar ridge',
      postalveolar: 'the area just behind the ridge',
      palate: 'the hard palate',
      velum: 'the soft palate',
      uvula: 'the uvula',
      pharynx: 'the back wall of the pharynx',
    },
    closed: (part, against) => `${part} presses against ${against} and closes off the air.`,
    closedMid: (part, against) => `${part} presses against ${against} and closes off the air in the middle.`,
    narrow: (part, near) => `${part} is close to ${near}: the air passes through a narrow gap.`,
    lipsClosed: 'The lips are closed.',
    labiodental: 'The lower lip is against the upper teeth.',
    open: 'The air passes freely through the mouth.',
    groove: 'A narrow groove runs down the middle of the tongue.',
    sideOne: 'The air escapes at one side.',
    sideBoth: 'The air escapes at both sides.',
    tipDown: 'The tongue tip is behind the lower teeth.',
    rounded: 'The lips are rounded.',
    spread: 'The lips are spread.',
    nasal: 'The soft palate is lowered, so the air goes through the nose.',
    voice: { open: 'The vocal folds are apart: the sound is voiceless.', vibrating: 'The vocal folds vibrate.', closed: 'The vocal folds are closed.' },
    trill: 'The tongue tip vibrates against the alveolar ridge.',
    touches: (what) => `The tongue touches ${what}.`,
    noTouch: 'The tongue does not touch the hard palate.',
    zones: {
      ridge: 'the alveolar ridge',
      behindRidge: 'just behind the ridge',
      palate: 'the hard palate',
      back: 'the back edge of the hard palate',
      sides: 'the sides of the palate',
    },
    and: 'and',
    behind: 'Further back the tongue touches the soft palate (estimate, not measured).',
    teethShow: 'The upper teeth show.',
    mouthOpen: 'The lips are apart.',
  },
  credits: {
    title: 'Sources of the mouth pictures',
    mocha:
      'The palate contact maps are computed from the MOCHA-TIMIT database, in which the tongue’s contact with the palate was measured with an artificial palate (electropalatography): speakers msak0 and fsew0, Alan Wrench, Queen Margaret University College 1999. Modified data: the pictures show each sound’s average, not the original measurements. For research, educational and individual use only.',
    usc: 'The side view’s proportions and the tongue positions are measured from the USC 75-speaker real-time MRI dataset (Lim et al. 2021), licence CC BY 4.0. Outlines were traced from the images; the images themselves are not in the app.',
    literature: 'For sounds English does not have (gh, q, lj, ñ, the trills) the positions are drawn from descriptions in the phonetic literature.',
    licence: 'MOCHA-TIMIT licence',
  },
};

// Messages.coachMouth (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('coachMouth', coachMouthFi, coachMouthEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof coachMouthFi> {}
}
